import React, { useRef, useState, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Loader } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import toast from 'react-hot-toast';
import adminApi from '../../services/adminApi';
import logger from '../../utils/logger';

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  onRemove?: () => void;
  folder?: string;
  maxSize?: number; // in MB
  aspectRatio?: string;
  className?: string;
  label?: string;
  description?: string;
  withThumbnail?: boolean;
  onUploadingChange?: (isUploading: boolean) => void;
}

const URL_KEYS = ['url', 'imageUrl', 'originalUrl'];

const pickUrlFromRecord = (record: Record<string, unknown>): string | null => {
  for (const key of URL_KEYS) {
    const val = record[key];
    if (typeof val === 'string' && val.trim() !== '') {
      return val;
    }
  }
  return null;
};

const extractImageUrlFromObject = (obj: unknown): string | null => {
  if (typeof obj !== 'object' || obj === null) return null;
  const record = obj as Record<string, unknown>;
  
  const direct = pickUrlFromRecord(record);
  if (direct) return direct;

  for (const key of ['data', 'result', 'payload']) {
    const nested = record[key];
    if (typeof nested === 'object' && nested !== null) {
      const found = pickUrlFromRecord(nested as Record<string, unknown>);
      if (found) return found;
    }
  }
  return null;
};

const extractImageUrl = (response: unknown): string => {
  if (typeof response === 'string' && response.trim() !== '') {
    return response;
  }
  if (typeof response === 'object' && response !== null) {
    const url = extractImageUrlFromObject(response);
    if (url) return url;
  }
  throw new Error('Upload succeeded but no image URL found in response');
};

export const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  onRemove,
  folder = 'uploads',
  maxSize = 5,
  aspectRatio,
  className = '',
  label = 'Upload Image',
  description,
  withThumbnail = false,
  onUploadingChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | undefined>(value);

  useEffect(() => {
    setPreview(value);
  }, [value]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSize) {
      toast.error(`File size must be less than ${maxSize}MB`);
      return;
    }

    try {
      setIsUploading(true);
      onUploadingChange?.(true);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Upload to server
      const response = withThumbnail
        ? await adminApi.uploadImageWithThumbnail(file, folder)
        : await adminApi.uploadImage(file, folder);

      const imageUrl = extractImageUrl(response);
      onChange(imageUrl);
      toast.success('Image uploaded successfully');
    } catch (error) {
      logger.error('Image upload failed:', error);
      toast.error('Failed to upload image');
      setPreview(undefined);
    } finally {
      setIsUploading(false);
      onUploadingChange?.(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    setPreview(undefined);
    onChange('');
    if (onRemove) {
      onRemove();
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="text-sm font-medium text-foreground">
          {label}
        </label>
      )}
      
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}

      <Card className="border-2 border-dashed border-muted-foreground/20 hover:border-primary/50 transition-colors">
        <CardContent className="p-4">
          {preview ? (
            <div className="relative group">
              <img
                src={preview}
                alt="Upload preview"
                className={`w-full h-auto rounded-md object-cover ${
                  aspectRatio ? `aspect-${aspectRatio}` : 'max-h-64'
                }`}
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-md flex items-center justify-center gap-2">
                <Button
                  type="button"
                  variant="blueBg"
                  size="sm"
                  onClick={handleClick}
                  disabled={isUploading}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Change
                </Button>
                <Button
                  type="button"
                  variant="red"
                  size="sm"
                  onClick={handleRemove}
                  disabled={isUploading}
                >
                  <X className="h-4 w-4 mr-2" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleClick}
              disabled={isUploading}
              className="w-full p-8 flex flex-col items-center justify-center gap-3 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <>
                  <Loader className="h-12 w-12 animate-spin text-primary" />
                  <span className="text-sm font-medium">Uploading...</span>
                </>
              ) : (
                <>
                  <ImageIcon className="h-12 w-12" />
                  <div className="text-center">
                    <p className="text-sm font-medium">Click to upload</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Max size: {maxSize}MB • PNG, JPG, WEBP
                    </p>
                  </div>
                </>
              )}
            </button>
          )}
        </CardContent>
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};
